import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { CompIcon } from 'common/react/components/CompIcon';
import { DrawerBottom } from 'common/react/components/drawer';
import { getTranslation } from 'common/react/utils/translations';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {Function} [props.selectedItemToString] - The selected item's text displayed on the input
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {string} [props.className] - Custom className added to the top `select` element
 * @returns {React.Element} Component.
 */
export const SelectMobileWithDefault = (props) => {
    const selectRef = useRef();

    const {
        selectedItem,
        getToggleButtonProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        getLabelProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );
            setIsOpen(false);
            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        }
    });

    const currentRound =
        typeof props?.currentRoundIndex !== 'undefined'
            ? props.currentRoundIndex
            : false;

    const isDisabled = props?.isDisabled;

    // remove our default item from the list
    const defaultIndex = props.items.indexOf(props.defaultItem);
    if (defaultIndex > -1) {
        props.items.splice(defaultIndex, 1);
    }

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    const getSelectedItemText = (item) => {
        switch (true) {
            case !!props.selectedItemToString:
                return props.selectedItemToString(item);
            case !!props.itemToString:
                return props.itemToString(item);
            default:
                return item.name;
        }
    };

    const selectedComp = useMemo(() => {
        if (selectedItem?.providerId && props.showCompLogo) {
            return PULSE.app.common.competition.getComp(
                selectedItem?.providerId
            );
        }

        return null;
    }, [selectedItem, props.showCompLogo]);

    const [isOpen, setIsOpen] = useState(false);

    const toggleButtonIcon = () => {
        if (selectedItem?.default && props.icon) {
            return (
                <span className={`competition-nav__icon`}>
                    <SvgIcon icon={props.icon} className="icon" />
                </span>
            );
        } else if (props.showCompLogo) {
            return (
                <span
                    className={`competition-nav__comp-icon competition-nav__comp-icon--${
                        selectedComp?.logoCode?.toLowerCase() ??
                        selectedComp?.code?.toLowerCase() ??
                        selectedItem?.logoCode?.toLowerCase() ??
                        selectedItem?.code?.toLowerCase() ??
                        'afl'
                    }`}
                >
                    <CompIcon
                        providerId={
                            selectedComp?.providerId ?? selectedItem?.providerId
                        }
                        className="icon"
                    />
                </span>
            );
        } else if (props.icon) {
            return (
                <span className={`competition-nav__icon`}>
                    <SvgIcon
                        icon={props.icon}
                        className="icon icon--placeholder"
                    />
                </span>
            );
        }

        return null;
    };

    return (
        <>
            <div className={`select ${props?.className ?? ''}`} ref={selectRef}>
                <div
                    className={`select__display-wrapper ${
                        selectedItem?.id !== -1 && selectedItem?.value !== -1
                            ? 'select__display-wrapper--has-selected-item'
                            : ''
                    }`}
                >
                    <button
                        type="button"
                        {...getToggleButtonProps({
                            id: `${PULSE.app.common.idifyString(
                                props.label
                            )}-mobile-dropdown-button`,
                            'aria-labelledby': !props.hideLabel
                                ? `${PULSE.app.common.idifyString(
                                      props.label
                                  )}-mobile-dropdown-label`
                                : false,
                            'aria-label': props.hideLabel ? props.label : false
                        })}
                        className="select__display"
                        onClick={() => setIsOpen(true)}
                        disabled={isDisabled}
                    >
                        {toggleButtonIcon()}
                        <span className="text-wrapper">
                            {props.label &&
                            !props.selectedItemToString &&
                            !props.hideLabel ? (
                                <label
                                    {...getLabelProps({
                                        id: `${PULSE.app.common.idifyString(
                                            props.label
                                        )}-mobile-dropdown-label`
                                    })}
                                    className="select__label"
                                >
                                    {props.label}
                                </label>
                            ) : null}
                            <span className="select__current">
                                <span className="select__current-text">
                                    {getSelectedItemText(selectedItem) || ''}
                                </span>
                            </span>
                        </span>

                        <SvgIcon
                            icon="chevron-down"
                            className={classNames('select__chevron', {
                                'select__chevron--open': isOpen
                            })}
                        />
                    </button>
                </div>
            </div>

            <DrawerBottom
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                heading={props.title}
            >
                <div className="select select--mobile select--open">
                    {/*
                        An invisible label for accessibility purposes.
                    */}
                    <label className="u-hide" {...getLabelProps()}>
                        {props.label}
                    </label>

                    {/*
                        An invisible dummy toggle button just so we're calling
                        getToggleButtonProps to ensure rc-drawer doesn't crash.
                    */}
                    <button
                        className="u-hide"
                        {...getToggleButtonProps()}
                    ></button>
                    <div className="select__options-wrapper">
                        <ul
                            {...getMenuProps(
                                {
                                    'aria-labelledby': `${PULSE.app.common.idifyString(
                                        props.label
                                    )}-mobile-dropdown-label`
                                },
                                { suppressRefError: true }
                            )}
                            className={classNames('select__options-list', {
                                'u-hide': !isOpen
                            })}
                        >
                            {props.items.map((item, index) => {
                                const itemText = getItemText(item);
                                const isSelected =
                                    getItemText(selectedItem) === itemText;
                                const compObj =
                                    PULSE.app.common.competition.getComp(
                                        item?.providerId
                                    );
                                return (
                                    <li
                                        key={`${itemText}${index}`}
                                        {...getItemProps({ item, index })}
                                        className={classNames(
                                            'select__option',
                                            {
                                                'select__option--selected':
                                                    isSelected,
                                                'select__option--current':
                                                    currentRound === index,
                                                'select__option--highlighted':
                                                    highlightedIndex === index
                                            }
                                        )}
                                    >
                                        {props.showCompLogo &&
                                        props.showCompLogoInside &&
                                        selectedItem ? (
                                            <div className="select__option-logo-name-wrapper">
                                                <span
                                                    className={`select__option-logo-icon--comp competition-nav__comp-icon competition-nav__comp-icon--internal competition-nav__comp-icon--${
                                                        compObj?.code?.toLowerCase() ??
                                                        compObj?.logoCode?.toLowerCase() ??
                                                        item?.logoCode.toLowerCase() ??
                                                        item?.code.toLowerCase() ??
                                                        'afl'
                                                    }`}
                                                >
                                                    <CompIcon
                                                        providerId={
                                                            compObj?.providerId ??
                                                            item?.providerId
                                                        }
                                                        className="icon"
                                                    />
                                                </span>
                                                <span>{itemText}</span>
                                            </div>
                                        ) : (
                                            <span>{itemText}</span>
                                        )}

                                        {isSelected && (
                                            <SvgIcon
                                                icon="checkmark"
                                                className="select__option-button-icon"
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        {props.defaultItem?.id !== selectedItem?.id &&
                        props.onReset ? (
                            <div className="select__header">
                                <button
                                    type="button"
                                    className="select__header-clear button-new button-new--secondary u-margin-left-auto"
                                    onClick={props.onReset}
                                >
                                    {getTranslation('label.clear')}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </DrawerBottom>
        </>
    );
};

SelectMobileWithDefault.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    selectedItemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    className: PropTypes.string,
    title: PropTypes.string,
    showCompLogoInside: PropTypes.bool,
    icon: PropTypes.string,
    onReset: PropTypes.func,
    defaultItem: PropTypes.object,
    hideLabel: PropTypes.bool
};
